import { useEffect, useMemo } from "react";
import { type GridRowId } from "@mui/x-data-grid-pro";
import { toast } from "react-toastify";
import { type BulkPropertyRow } from "../types";
import { type AcquirePolicy } from "../utils/acquirePolicy";
import {
  ALL_MATTERS_FIELDS,
  CHECKBOX_FIELDS,
  CLOSURE_EXCLUSIVE_FIELDS,
} from "../utils/checkboxFields";
import { isBulkCellDisabled } from "../utils/cellAvailability";
import {
  eligibleZenbujikoConstructions,
  matchesAnyConstruction,
} from "../utils/zenbujikoConstruction";
import { useCheckedField, type CheckedIdsStore } from "./useCheckedIdsStore";

/**
 * 行・取得条件の変化にチェック状態を追従させる。
 * 不可になったセルのチェックが残ると、送信対象に見えたまま依頼から落ちるため、
 * 閉鎖の排他・条件変更・全部事項の構成一致の3観点で解除する。
 * 未取得のみを取得する指定に切り替えた場合も、この追従で取得済みのチェックが外れる。
 * 条件変更による解除は操作したセルと離れた場所で起きるため、件数をトーストで知らせる
 * （閉鎖の排他は押した行の中で起きるため知らせない）
 */
export const useCheckedIdsSync = (
  checkedStore: CheckedIdsStore,
  rowsById: Map<GridRowId, BulkPropertyRow>,
  policy: AcquirePolicy,
  pastDays: number,
  excludeAcquired: boolean
): void => {
  const closureCheckedIds = useCheckedField(checkedStore, "requestClosureBook");

  // 閉鎖にチェックした行は、排他対象列のチェックを解除する（既存画面と同じく、閉鎖側からの一方向のみで逆向きには解除しない）
  useEffect(() => {
    if (closureCheckedIds.size === 0) {
      return;
    }
    for (const field of CLOSURE_EXCLUSIVE_FIELDS) {
      checkedStore.removeWhere(field, (id) => closureCheckedIds.has(id));
    }
  }, [closureCheckedIds, checkedStore]);

  // 取得条件・経過日数の変更で満たせなくなったセルのチェックを解除する（チェック済み集合のみ走査）
  useEffect(() => {
    // 取得手段が0個はフォーム編集中の一時的な無効状態（送信はバリデーションで止まる）。
    // ここで解除すると全チェックを落として戻せなくなるため何もしない
    if (policy.steps.length === 0) {
      return;
    }
    const now = new Date();
    let removedCount = 0;
    for (const { field } of CHECKBOX_FIELDS) {
      removedCount += checkedStore.removeWhere(field, (id) => {
        const row = rowsById.get(id);
        return (
          row == null ||
          isBulkCellDisabled(
            field,
            row,
            policy,
            pastDays,
            closureCheckedIds,
            now,
            excludeAcquired
          )
        );
      });
    }

    // 全部事項は候補の構成単位でしかリユースできないため、どの候補構成とも
    // 一致しなくなった行は4列まとめて解除する（構成の一部だけ残ると送信時に対象外になる）
    const invalidIds = new Set<GridRowId>();
    for (const id of new Set(
      ALL_MATTERS_FIELDS.flatMap((field) => [...checkedStore.getField(field)])
    )) {
      const row = rowsById.get(id);
      if (row == null) {
        continue;
      }
      const constructions = eligibleZenbujikoConstructions(
        row,
        policy,
        pastDays,
        now,
        excludeAcquired
      );
      if (constructions.length === 0) {
        continue;
      }
      const current = checkedStore.getRowFields(ALL_MATTERS_FIELDS, id);
      if (!matchesAnyConstruction(constructions, current)) {
        invalidIds.add(id);
      }
    }
    for (const field of ALL_MATTERS_FIELDS) {
      removedCount += checkedStore.removeWhere(field, (id) =>
        invalidIds.has(id)
      );
    }
    if (removedCount > 0) {
      toast.info(
        `条件を満たさなくなった${removedCount}件のチェックを解除しました`
      );
    }
  }, [
    policy,
    pastDays,
    excludeAcquired,
    rowsById,
    checkedStore,
    closureCheckedIds,
  ]);
};

/** 行 ID から行を引く索引。チェック済み集合の走査で毎回配列を検索しないために持つ */
export const useRowsById = (
  rows: BulkPropertyRow[] | null
): Map<GridRowId, BulkPropertyRow> =>
  useMemo(
    () =>
      new Map<GridRowId, BulkPropertyRow>(
        (rows ?? []).map((row) => [row.id, row])
      ),
    [rows]
  );
